import { useEffect, useState, type ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { Me } from '@pb/shared';
import { ApiError, setUnauthenticatedHandler } from './api/client';
import { getMe } from './api/auth';
import { SessionContext } from './lib/session';
import { AppLayout } from './components/AppLayout';
import { ErrorState, Loading, NotFound } from './components/States';
import { LoginPage } from './pages/LoginPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { AccountPage } from './pages/AccountPage';
import { ProjectShell } from './pages/ProjectShell';
import { LibraryPage } from './pages/LibraryPage';
import { ImportPage } from './pages/ImportPage';
import { SlidePage } from './pages/SlidePage';
import { GroupsPage } from './pages/GroupsPage';
import { GroupPage } from './pages/GroupPage';
import { PresentationsPage } from './pages/PresentationsPage';
import { PresentationPage } from './pages/PresentationPage';
import { TemplatePage } from './pages/TemplatePage';
import { EventsPage } from './pages/EventsPage';
import { EventPage } from './pages/EventPage';
import { ProjectHistoryPage } from './pages/ProjectHistoryPage';

type SessionStatus = 'checking' | 'ready' | 'error';

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [status, setStatus] = useState<SessionStatus>('checking');
  const [bootError, setBootError] = useState<unknown>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const check = async () => {
    setStatus('checking');
    try {
      setMe(await getMe());
      setStatus('ready');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setMe(null);
        setStatus('ready');
        return;
      }
      setBootError(err);
      setStatus('error');
    }
  };

  useEffect(() => {
    void check();
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setMe(null);
      const here = window.location.pathname + window.location.search;
      if (window.location.pathname === '/login') return;
      navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true });
    });
  }, [navigate]);

  if (status === 'checking') return <Loading label="Starting…" />;
  if (status === 'error') return <ErrorState error={bootError} onRetry={() => void check()} />;

  const next = encodeURIComponent(location.pathname + location.search);
  const guard = (element: ReactElement) => (me ? element : <Navigate to={`/login?next=${next}`} replace />);

  return (
    <SessionContext.Provider value={{ me, setMe }}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={guard(<AppLayout />)}>
          <Route path="/" element={<ProjectsPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/projects/:projectId" element={<ProjectShell />}>
            <Route index element={<Navigate to="presentations" replace />} />
            <Route path="presentations" element={<PresentationsPage />} />
            <Route path="presentations/:presentationId" element={<PresentationPage />} />
            <Route path="library" element={<LibraryPage />} />
            <Route path="imports/:importId" element={<ImportPage />} />
            <Route path="slides/:slideId" element={<SlidePage />} />
            <Route path="groups" element={<GroupsPage />} />
            <Route path="groups/:groupId" element={<GroupPage />} />
            <Route path="events" element={<EventsPage />} />
            <Route path="events/:eventId" element={<EventPage />} />
            <Route path="template" element={<TemplatePage />} />
            <Route path="history" element={<ProjectHistoryPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </SessionContext.Provider>
  );
}
